import * as React from 'react'
import styled from 'styled-components'
import {
  BrowserLocaleDateFormatter,
  CallDocument,
  ConfirmAction,
  FlowCall,
  Input,
  NewButton,
  NewModal,
  NotificationType,
  SvgIcon,
  truncateFilename,
} from 'prace-common-components'
import { useAppDispatch } from 'store/hooks'
import {
  useGetCallDocumentsQuery,
  useUploadCallDocumentMutation,
  useRenameCallDocumentMutation,
  useDeleteCallDocumentMutation,
} from 'store/api/calls'

const ACCEPTED_TYPES = '.pdf,.png,.jpg,.jpeg,.webp'
/* Mirrors `pdfAndImageBatchUpload`'s files limit — busboy aborts the whole request past it */
const MAX_FILES_PER_UPLOAD = 10

const $Container = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
`

/* Same row treatment as the read-only CallDocuments list users see on the call page */
const $Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(1)} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.greyScales[100]};
`

/* Only the staged files being named before upload use an inline field; renaming an existing document
   goes through the modal, so the list stays a list. */
const $NameInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: ${({ theme }) => theme.spacing(0.5)};
  font-family: inherit;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.headLine};
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  &:hover {
    border-color: ${({ theme }) => theme.colors.greyScales[100]};
  }
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

/* Icon lives inside the anchor so it downloads too, and inherits the hover colour via currentColor */
const $Link = styled.a`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.headLine};
  cursor: pointer;
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`

const $Filename = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const $Date = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
`

const $Empty = styled.p`
  color: ${({ theme }) => theme.colors.greyScales[800]};
`

const $Hint = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.greyScales[800]};
`

const $HiddenInput = styled.input`
  display: none;
`

/* NewModal renders only a title bar, so the body owns its own padding and width */
const $ModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing(2)};
  min-width: 420px;
`

const $ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(2)};
`

const $Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(2)};
`

type StagedFile = { file: File; name: string }

/**
 * Documents attached to a call. Uploads, renames and deletions apply immediately — they do not go
 * through the modal's Save changes button.
 */
export const EditCallDocuments: React.FC<{ callId: FlowCall['id'] }> = ({ callId }) => {
  const dispatch = useAppDispatch()
  const { data: documents } = useGetCallDocumentsQuery(callId)
  const [uploadCallDocument] = useUploadCallDocumentMutation()
  const [renameCallDocument] = useRenameCallDocumentMutation()
  const [deleteCallDocument] = useDeleteCallDocumentMutation()
  const uploadRef = React.useRef<HTMLInputElement>(null)
  const [staged, setStaged] = React.useState<StagedFile[]>([])
  /* The document being renamed, and the name typed so far. Null closes the modal. */
  const [editing, setEditing] = React.useState<CallDocument | null>(null)
  const [draftName, setDraftName] = React.useState('')

  const notify = (type: NotificationType, msg: string) =>
    dispatch({ type: 'notification', payload: { type, msg } })

  const handleSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : []
    event.target.value = ''
    if (!files.length) return
    /* Caught here so the count is named, instead of the request dying as a generic upload failure */
    if (files.length > MAX_FILES_PER_UPLOAD) {
      notify(NotificationType.error, `Please upload at most ${MAX_FILES_PER_UPLOAD} documents at a time`)
      return
    }
    /* Names start empty: an unnamed document falls back to its filename, so blank is a valid choice */
    setStaged(files.map((file) => ({ file, name: '' })))
  }

  const handleUpload = async () => {
    /* One request for the whole selection — the API saves them in a single transaction, so a rejected
       file (wrong type, too large) fails the batch instead of leaving some of them attached. */
    const data = new FormData()
    /* Names first so they are parsed before the file stream, and positional: the API pairs
       `names[i]` with `file[i]`, so both must be appended in the same order. */
    staged.forEach(({ name }) => data.append('names', name))
    staged.forEach(({ file }) => data.append('file', file))
    try {
      await uploadCallDocument({ id: callId, data }).unwrap()
      setStaged([])
      notify(NotificationType.success, 'Document(s) added')
    } catch {
      notify(NotificationType.error, 'Failed to add document(s)')
    }
  }

  const handleEdit = (document: CallDocument) => {
    setEditing(document)
    setDraftName(document.name || '')
  }

  const handleRename = async () => {
    if (!editing) return
    /* Nothing to send if the name came back unchanged — closing is the whole action */
    if (draftName.trim() === (editing.name || '').trim()) return setEditing(null)
    try {
      await renameCallDocument({ id: editing.id, name: draftName }).unwrap()
      setEditing(null)
      notify(NotificationType.success, 'Document name saved')
    } catch {
      notify(NotificationType.error, 'Failed to save the document name')
    }
  }

  const handleDelete = async (documentId: CallDocument['id']) => {
    try {
      await deleteCallDocument(documentId).unwrap()
      notify(NotificationType.success, 'Document removed')
    } catch {
      notify(NotificationType.error, 'Failed to remove document')
    }
  }

  return (
    <$Container>
      <$Hint>
        These documents are downloadable by anyone on the call page and from every application in this call.
        Users see the name given here — leave it empty to show the filename instead.
        Up to {MAX_FILES_PER_UPLOAD} files per upload, 10 MB each.
      </$Hint>
      {documents?.length ? documents.map((document) => (
        <$Row key={document.id}>
          {/* A named document shows its name; an unnamed one still shows the file it was uploaded as */}
          <$Link href={document.url} target='_blank' rel='noopener noreferrer' title={document.originalFilename}>
            <SvgIcon name='download' size={18} />
            <$Filename>{document.name || truncateFilename(document.originalFilename)}</$Filename>
          </$Link>
          <$Date>{BrowserLocaleDateFormatter.format(new Date(document.createdAt))}</$Date>
          <SvgIcon
            name='edit'
            size={18}
            color='#757778'
            clickable
            aria-label='Edit document name'
            onClick={() => handleEdit(document)}
          />
          <ConfirmAction
            deletion
            title='Remove document'
            description='The document will no longer be available to users on the call page or in applications.'
            onConfirm={() => handleDelete(document.id)}
          >
            <SvgIcon name='delete' size={18} color='#757778' clickable />
          </ConfirmAction>
        </$Row>
      )) : !staged.length && (
        <$Empty>No documents uploaded yet.</$Empty>
      )}
      {staged.map((item, index) => (
        <$Row key={`${item.file.name}-${index}`}>
          <$NameInput
            aria-label={`Name for ${item.file.name}`}
            autoFocus={!index}
            value={item.name}
            placeholder={item.file.name}
            onChange={(event) => setStaged((current) =>
              current.map((file, i) => i === index ? { ...file, name: event.target.value } : file))}
          />
          <$Filename title={item.file.name}>{truncateFilename(item.file.name)}</$Filename>
        </$Row>
      ))}
      <$HiddenInput
        ref={uploadRef}
        type='file'
        accept={ACCEPTED_TYPES}
        multiple
        onChange={handleSelect}
      />
      {!!editing && (
        <NewModal small open title='Edit document name' onClose={() => setEditing(null)}>
          <$ModalBody>
            <Input
              title='Document name'
              name='name'
              value={draftName}
              hideItalic
              /* Empty is valid: it clears the name and users see the filename again */
              description={`Leave empty to show the file name (${editing.originalFilename})`}
              onChange={(_name, value) => setDraftName(String(value ?? ''))}
            />
            <$ModalActions>
              <NewButton variant='outlined' onClick={() => setEditing(null)}>Cancel</NewButton>
              <NewButton onClick={handleRename}>Save</NewButton>
            </$ModalActions>
          </$ModalBody>
        </NewModal>
      )}
      <$Actions>
        {staged.length ? (
          <>
            <NewButton onClick={handleUpload}>Upload {staged.length} document(s)</NewButton>
            <NewButton variant='outlined' onClick={() => setStaged([])}>Cancel</NewButton>
          </>
        ) : (
          <NewButton variant='outlined' onClick={() => uploadRef.current?.click()}>
            Upload document(s)
          </NewButton>
        )}
      </$Actions>
    </$Container>
  )
}
